import { create } from 'zustand';
import type { ViewMode, BaseLayer } from '@/types';

interface AtlasState {
  viewMode: ViewMode;
  baseLayer: BaseLayer;
  orderPolygon: GeoJSON.Polygon | null;
  orderBounds: [number, number, number, number] | null;
  showPolygon: boolean;
  terrainExaggeration: number;
  loading: boolean;
  error: string | null;
  // Lazy layer loading (raster products fetch metadata+tiles only when toggled on)
  rasterAvailable: Record<string, boolean>;
  layerLoading: Record<string, boolean>;
  loadLayer: ((key: string) => void) | null;
  setRasterAvailable: (m: Record<string, boolean>) => void;
  setLayerLoading: (key: string, v: boolean) => void;
  setLoadLayer: (fn: ((key: string) => void) | null) => void;
  mapCenter: [number, number];
  mapZoom: number;

  // Create-order (in-map draw) flow
  createMode: boolean;            // the new-order panel is open on the map
  drawing: boolean;               // actively capturing map clicks as polygon vertices
  draftPoints: [number, number][]; // [lng, lat] vertices of the polygon being drawn
  draftGeometry: GeoJSON.Polygon | null; // polygon from an uploaded file (alternative to draw)
  setCreateMode: (b: boolean) => void;
  setDrawing: (b: boolean) => void;
  addDraftPoint: (pt: [number, number]) => void;
  undoDraftPoint: () => void;
  clearDraft: () => void;
  setDraftGeometry: (poly: GeoJSON.Polygon | null) => void;

  // MDT layer
  mdtVisible: boolean;
  mdtTileUrl: string | null;
  // Terrarium-encoded DEM tiles (TiTiler algorithm=terrarium) for the native
  // MapLibre hillshade layer that shades the MDT colour ramp (relief overlay).
  mdtDemTileUrl: string | null;
  mdtRescale: string | null;
  mdtZoomRange: { minzoom: number; maxzoom: number } | null;
  mdtOpacity: number;

  // MDT Local layer (polygon-clipped rescale)
  mdtLocalVisible: boolean;
  mdtLocalTileUrl: string | null;
  mdtLocalZoomRange: { minzoom: number; maxzoom: number } | null;
  mdtLocalOpacity: number;

  // MDT DGT layer (from LOLA 50cm LiDAR)
  mdtDgtVisible: boolean;
  mdtDgtTileUrl: string | null;
  mdtDgtRescale: string | null;
  mdtDgtZoomRange: { minzoom: number; maxzoom: number } | null;
  mdtDgtOpacity: number;

  // MDT DGT Local layer (polygon-clipped rescale)
  mdtDgtLocalVisible: boolean;
  mdtDgtLocalTileUrl: string | null;
  mdtDgtLocalZoomRange: { minzoom: number; maxzoom: number } | null;
  mdtDgtLocalOpacity: number;

  // Hillshade layer
  hillshadeVisible: boolean;
  hillshadeTileUrl: string | null;
  hillshadeZoomRange: { minzoom: number; maxzoom: number } | null;
  hillshadeOpacity: number;

  // Hillshade Local layer (polygon-clipped rescale)
  hillshadeLocalVisible: boolean;
  hillshadeLocalTileUrl: string | null;
  hillshadeLocalZoomRange: { minzoom: number; maxzoom: number } | null;
  hillshadeLocalOpacity: number;

  // Hillshade DGT layer (from LOLA 50cm LiDAR)
  hillshadeDgtVisible: boolean;
  hillshadeDgtTileUrl: string | null;
  hillshadeDgtZoomRange: { minzoom: number; maxzoom: number } | null;
  hillshadeDgtOpacity: number;

  // Hillshade DGT Local layer
  hillshadeDgtLocalVisible: boolean;
  hillshadeDgtLocalTileUrl: string | null;
  hillshadeDgtLocalZoomRange: { minzoom: number; maxzoom: number } | null;
  hillshadeDgtLocalOpacity: number;

  // Hillshade Hypsometric layer
  hillshadeHypsoVisible: boolean;
  hillshadeHypsoTileUrl: string | null;
  hillshadeHypsoZoomRange: { minzoom: number; maxzoom: number } | null;
  hillshadeHypsoOpacity: number;

  // Hillshade Hypsometric DGT layer (from LOLA 50cm)
  hillshadeHypsoDgtVisible: boolean;
  hillshadeHypsoDgtTileUrl: string | null;
  hillshadeHypsoDgtZoomRange: { minzoom: number; maxzoom: number } | null;
  hillshadeHypsoDgtOpacity: number;

  // Hillshade Hypsometric DGT Local layer
  hillshadeHypsoDgtLocalVisible: boolean;
  hillshadeHypsoDgtLocalTileUrl: string | null;
  hillshadeHypsoDgtLocalZoomRange: { minzoom: number; maxzoom: number } | null;
  hillshadeHypsoDgtLocalOpacity: number;

  // Hillshade Hypsometric Local layer (polygon-clipped per-band rescale)
  hillshadeHypsoLocalVisible: boolean;
  hillshadeHypsoLocalTileUrl: string | null;
  hillshadeHypsoLocalZoomRange: { minzoom: number; maxzoom: number } | null;
  hillshadeHypsoLocalOpacity: number;

  // Contours AWS (vector tiles via Martin — from AWS 30m DTM)
  contoursVisible: boolean;
  contoursTileUrl: string | null;
  contoursSourceLayer: string | null;
  contoursItemId: string | null;
  contoursOpacity: number;

  // Contours DGT (vector tiles via Martin — from LOLA 50cm/2m DTM)
  contoursDgtVisible: boolean;
  contoursDgtTileUrl: string | null;
  contoursDgtSourceLayer: string | null;
  contoursDgtItemId: string | null;
  contoursDgtOpacity: number;

  // Slope (Declives) — Morfologia group
  slopeVisible: boolean;
  slopeTileUrl: string | null;
  slopeZoomRange: { minzoom: number; maxzoom: number } | null;
  slopeOpacity: number;

  // Aspect (Exposição Solar) — Morfologia group
  aspectVisible: boolean;
  aspectTileUrl: string | null;
  aspectZoomRange: { minzoom: number; maxzoom: number } | null;
  aspectOpacity: number;

  // Geomorphons (10 Landforms) — Morfologia group
  geomorphonsVisible: boolean;
  geomorphonsTileUrl: string | null;
  geomorphonsZoomRange: { minzoom: number; maxzoom: number } | null;
  geomorphonsOpacity: number;

  // Erosion Risk (Risco de Erosão) — Erosão group
  erosionRiskVisible: boolean;
  erosionRiskTileUrl: string | null;
  erosionRiskZoomRange: { minzoom: number; maxzoom: number } | null;
  erosionRiskOpacity: number;

  // Wind Exposition (Exposição ao Vento) — Morfologia group
  windExpositionVisible: boolean;
  windExpositionTileUrl: string | null;
  windExpositionZoomRange: { minzoom: number; maxzoom: number } | null;
  windExpositionOpacity: number;
  // Companion hillshade gamma 1 for wind exposition underlay
  windHillshadeTileUrl: string | null;
  windHillshadeZoomRange: { minzoom: number; maxzoom: number } | null;
  windReliefTileUrl: string | null;
  windReliefZoomRange: { minzoom: number; maxzoom: number } | null;

  // Streams (Linhas de Água) — Hidrologia group
  streamsVisible: boolean;
  streamsTileUrl: string | null;
  streamsSourceLayer: string | null;
  streamsItemId: string | null;
  streamsOpacity: number;

  // Flow Accumulation (Acumulação) — Hidrologia group
  flowAccVisible: boolean;
  flowAccTileUrl: string | null;
  flowAccZoomRange: { minzoom: number; maxzoom: number } | null;
  flowAccOpacity: number;

  // Basins (Bacias Hidrográficas) — Hidrologia group
  basinsVisible: boolean;
  basinsTileUrl: string | null;
  basinsSourceLayer: string | null;
  basinsItemId: string | null;
  basinsOpacity: number;

  // Keypoints (Inflexão de Vale) — Landforms & Keyline group
  keypointsVisible: boolean;
  keypointsTileUrl: string | null;
  keypointsSourceLayer: string | null;
  keypointsItemId: string | null;
  keypointsOpacity: number;

  // Primary Valleys (Geomorphon class 9) — Landforms & Keyline group
  primaryValleysVisible: boolean;
  primaryValleysTileUrl: string | null;
  primaryValleysSourceLayer: string | null;
  primaryValleysItemId: string | null;
  primaryValleysOpacity: number;

  // Primary Ridges (Geomorphon class 3) — Landforms & Keyline group
  primaryRidgesVisible: boolean;
  primaryRidgesTileUrl: string | null;
  primaryRidgesSourceLayer: string | null;
  primaryRidgesItemId: string | null;
  primaryRidgesOpacity: number;

  // Flood Zones (Áreas Inundáveis) — Hidrologia group
  floodZonesVisible: boolean;
  floodZonesTileUrl: string | null;
  floodZonesSourceLayer: string | null;
  floodZonesItemId: string | null;
  floodZonesOpacity: number;

  // === OSM Context Layers (PMTiles, always available) ===
  roadsVisible: boolean;
  roadsOpacity: number;
  buildingsVisible: boolean;
  buildingsOpacity: number;
  landuseVisible: boolean;
  landuseOpacity: number;
  waterwaysVisible: boolean;
  waterwaysOpacity: number;
  adminBoundsVisible: boolean;
  adminBoundsOpacity: number;

  // Ridges (Festos / Cumeadas) — Hidrologia group
  ridgesVisible: boolean;
  ridgesTileUrl: string | null;
  ridgesSourceLayer: string | null;
  ridgesItemId: string | null;
  ridgesOpacity: number;

  // === Institutional layers (Cadastro + Restricoes Legais groups) ===
  // Bundled global context layers. itemId stays null (no per-order filter).
  cadastroDgtVisible: boolean;
  cadastroDgtTileUrl: string | null;
  cadastroDgtSourceLayer: string | null;
  cadastroDgtItemId: string | null;
  cadastroDgtOpacity: number;
  // Bbox [w,s,e,n] (EPSG:4326) the cadastro source is bounded to: union extent
  // of parcels intersecting the order area. null until the order-load fetch
  // resolves; the layer add waits for it (source bounds is immutable once set).
  cadastroBounds: [number, number, number, number] | null;

  bupiRggVisible: boolean;
  bupiRggTileUrl: string | null;
  bupiRggSourceLayer: string | null;
  bupiRggItemId: string | null;
  bupiRggOpacity: number;

  // CAOP shares one Martin source (caop_admin) but renders as two map layers
  // filtered by `tipo`. So they share tileUrl + sourceLayer but track separate
  // visible + opacity.
  freguesiasVisible: boolean;
  freguesiasTileUrl: string | null;
  freguesiasSourceLayer: string | null;
  freguesiasItemId: string | null;
  freguesiasOpacity: number;

  concelhosVisible: boolean;
  concelhosTileUrl: string | null;
  concelhosSourceLayer: string | null;
  concelhosItemId: string | null;
  concelhosOpacity: number;

  // Restricoes Legais. ren + renExclusoes share one source (geo.ren) but
  // render as two map layers filtered by `tipologia`. ran is its own source.
  // tileUrl + sourceLayer + itemId swap when an order has restricoes-legais
  // ready: source becomes order_restricoes_legais filtered by item.
  renVisible: boolean;
  renTileUrl: string | null;
  renSourceLayer: string | null;
  renItemId: string | null;
  renOpacity: number;

  renExclusoesVisible: boolean;
  renExclusoesTileUrl: string | null;
  renExclusoesSourceLayer: string | null;
  renExclusoesItemId: string | null;
  renExclusoesOpacity: number;

  ranVisible: boolean;
  ranTileUrl: string | null;
  ranSourceLayer: string | null;
  ranItemId: string | null;
  ranOpacity: number;

  setViewMode: (mode: ViewMode) => void;
  setBaseLayer: (layer: BaseLayer) => void;
  setOrderPolygon: (polygon: GeoJSON.Polygon | null) => void;
  setOrderBounds: (bounds: [number, number, number, number] | null) => void;
  setShowPolygon: (show: boolean) => void;
  setTerrainExaggeration: (value: number) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setMapCenter: (center: [number, number]) => void;
  setMapZoom: (zoom: number) => void;
  setMdtVisible: (visible: boolean) => void;
  setMdtTileUrl: (url: string | null) => void;
  setMdtDemTileUrl: (url: string | null) => void;
  setMdtRescale: (rescale: string | null) => void;
  setMdtZoomRange: (range: { minzoom: number; maxzoom: number } | null) => void;
  setMdtOpacity: (opacity: number) => void;
  setMdtLocalVisible: (visible: boolean) => void;
  setMdtLocalTileUrl: (url: string | null) => void;
  setMdtLocalZoomRange: (range: { minzoom: number; maxzoom: number } | null) => void;
  setMdtLocalOpacity: (opacity: number) => void;
  setMdtDgtVisible: (visible: boolean) => void;
  setMdtDgtTileUrl: (url: string | null) => void;
  setMdtDgtRescale: (rescale: string | null) => void;
  setMdtDgtZoomRange: (range: { minzoom: number; maxzoom: number } | null) => void;
  setMdtDgtOpacity: (opacity: number) => void;
  setMdtDgtLocalVisible: (visible: boolean) => void;
  setMdtDgtLocalTileUrl: (url: string | null) => void;
  setMdtDgtLocalZoomRange: (range: { minzoom: number; maxzoom: number } | null) => void;
  setMdtDgtLocalOpacity: (opacity: number) => void;

  setHillshadeVisible: (visible: boolean) => void;
  setHillshadeTileUrl: (url: string | null) => void;
  setHillshadeZoomRange: (range: { minzoom: number; maxzoom: number } | null) => void;
  setHillshadeOpacity: (opacity: number) => void;
  setHillshadeLocalVisible: (visible: boolean) => void;
  setHillshadeLocalTileUrl: (url: string | null) => void;
  setHillshadeLocalZoomRange: (range: { minzoom: number; maxzoom: number } | null) => void;
  setHillshadeLocalOpacity: (opacity: number) => void;
  setHillshadeDgtVisible: (visible: boolean) => void;
  setHillshadeDgtTileUrl: (url: string | null) => void;
  setHillshadeDgtZoomRange: (range: { minzoom: number; maxzoom: number } | null) => void;
  setHillshadeDgtOpacity: (opacity: number) => void;
  setHillshadeDgtLocalVisible: (visible: boolean) => void;
  setHillshadeDgtLocalTileUrl: (url: string | null) => void;
  setHillshadeDgtLocalZoomRange: (range: { minzoom: number; maxzoom: number } | null) => void;
  setHillshadeDgtLocalOpacity: (opacity: number) => void;
  setHillshadeHypsoVisible: (visible: boolean) => void;
  setHillshadeHypsoTileUrl: (url: string | null) => void;
  setHillshadeHypsoZoomRange: (range: { minzoom: number; maxzoom: number } | null) => void;
  setHillshadeHypsoOpacity: (opacity: number) => void;
  setHillshadeHypsoLocalVisible: (visible: boolean) => void;
  setHillshadeHypsoLocalTileUrl: (url: string | null) => void;
  setHillshadeHypsoLocalZoomRange: (range: { minzoom: number; maxzoom: number } | null) => void;
  setHillshadeHypsoLocalOpacity: (opacity: number) => void;
  setHillshadeHypsoDgtVisible: (visible: boolean) => void;
  setHillshadeHypsoDgtTileUrl: (url: string | null) => void;
  setHillshadeHypsoDgtZoomRange: (range: { minzoom: number; maxzoom: number } | null) => void;
  setHillshadeHypsoDgtOpacity: (opacity: number) => void;
  setHillshadeHypsoDgtLocalVisible: (visible: boolean) => void;
  setHillshadeHypsoDgtLocalTileUrl: (url: string | null) => void;
  setHillshadeHypsoDgtLocalZoomRange: (range: { minzoom: number; maxzoom: number } | null) => void;
  setHillshadeHypsoDgtLocalOpacity: (opacity: number) => void;
  setContoursVisible: (visible: boolean) => void;
  setContoursTileUrl: (url: string | null) => void;
  setContoursSourceLayer: (layer: string | null) => void;
  setContoursItemId: (id: string | null) => void;
  setContoursOpacity: (opacity: number) => void;
  setContoursDgtVisible: (visible: boolean) => void;
  setContoursDgtTileUrl: (url: string | null) => void;
  setContoursDgtSourceLayer: (layer: string | null) => void;
  setContoursDgtItemId: (id: string | null) => void;
  setContoursDgtOpacity: (opacity: number) => void;
  setSlopeVisible: (visible: boolean) => void;
  setSlopeTileUrl: (url: string | null) => void;
  setSlopeZoomRange: (range: { minzoom: number; maxzoom: number } | null) => void;
  setSlopeOpacity: (opacity: number) => void;
  setAspectVisible: (visible: boolean) => void;
  setAspectTileUrl: (url: string | null) => void;
  setAspectZoomRange: (range: { minzoom: number; maxzoom: number } | null) => void;
  setAspectOpacity: (opacity: number) => void;
  setGeomorphonsVisible: (visible: boolean) => void;
  setGeomorphonsTileUrl: (url: string | null) => void;
  setGeomorphonsZoomRange: (range: { minzoom: number; maxzoom: number } | null) => void;
  setGeomorphonsOpacity: (opacity: number) => void;
  setErosionRiskVisible: (visible: boolean) => void;
  setErosionRiskTileUrl: (url: string | null) => void;
  setErosionRiskZoomRange: (range: { minzoom: number; maxzoom: number } | null) => void;
  setErosionRiskOpacity: (opacity: number) => void;
  setWindExpositionVisible: (visible: boolean) => void;
  setWindExpositionTileUrl: (url: string | null) => void;
  setWindExpositionZoomRange: (range: { minzoom: number; maxzoom: number } | null) => void;
  setWindExpositionOpacity: (opacity: number) => void;
  setWindHillshadeTileUrl: (url: string | null) => void;
  setWindReliefTileUrl: (url: string | null) => void;
  setWindReliefZoomRange: (range: { minzoom: number; maxzoom: number } | null) => void;
  setWindHillshadeZoomRange: (range: { minzoom: number; maxzoom: number } | null) => void;
  setStreamsVisible: (visible: boolean) => void;
  setStreamsTileUrl: (url: string | null) => void;
  setStreamsSourceLayer: (layer: string | null) => void;
  setStreamsItemId: (id: string | null) => void;
  setStreamsOpacity: (opacity: number) => void;
  setFlowAccVisible: (visible: boolean) => void;
  setFlowAccTileUrl: (url: string | null) => void;
  setFlowAccZoomRange: (range: { minzoom: number; maxzoom: number } | null) => void;
  setFlowAccOpacity: (opacity: number) => void;
  setBasinsVisible: (visible: boolean) => void;
  setBasinsTileUrl: (url: string | null) => void;
  setBasinsSourceLayer: (layer: string | null) => void;
  setBasinsItemId: (id: string | null) => void;
  setBasinsOpacity: (opacity: number) => void;
  setKeypointsVisible: (visible: boolean) => void;
  setKeypointsTileUrl: (url: string | null) => void;
  setKeypointsSourceLayer: (layer: string | null) => void;
  setKeypointsItemId: (id: string | null) => void;
  setKeypointsOpacity: (opacity: number) => void;
  setPrimaryValleysVisible: (visible: boolean) => void;
  setPrimaryValleysTileUrl: (url: string | null) => void;
  setPrimaryValleysSourceLayer: (layer: string | null) => void;
  setPrimaryValleysItemId: (id: string | null) => void;
  setPrimaryValleysOpacity: (opacity: number) => void;
  setPrimaryRidgesVisible: (visible: boolean) => void;
  setPrimaryRidgesTileUrl: (url: string | null) => void;
  setPrimaryRidgesSourceLayer: (layer: string | null) => void;
  setPrimaryRidgesItemId: (id: string | null) => void;
  setPrimaryRidgesOpacity: (opacity: number) => void;
  setFloodZonesVisible: (visible: boolean) => void;
  setFloodZonesTileUrl: (url: string | null) => void;
  setFloodZonesSourceLayer: (layer: string | null) => void;
  setFloodZonesItemId: (id: string | null) => void;
  setFloodZonesOpacity: (opacity: number) => void;
  setRidgesVisible: (visible: boolean) => void;
  setRidgesTileUrl: (url: string | null) => void;
  setRidgesSourceLayer: (layer: string | null) => void;
  setRidgesItemId: (id: string | null) => void;
  setRidgesOpacity: (opacity: number) => void;
  // Institutional
  setCadastroDgtVisible: (visible: boolean) => void;
  setCadastroDgtTileUrl: (url: string | null) => void;
  setCadastroDgtSourceLayer: (layer: string | null) => void;
  setCadastroDgtItemId: (id: string | null) => void;
  setCadastroDgtOpacity: (opacity: number) => void;
  setCadastroBounds: (bounds: [number, number, number, number] | null) => void;
  setBupiRggVisible: (visible: boolean) => void;
  setBupiRggTileUrl: (url: string | null) => void;
  setBupiRggSourceLayer: (layer: string | null) => void;
  setBupiRggItemId: (id: string | null) => void;
  setBupiRggOpacity: (opacity: number) => void;
  setFreguesiasVisible: (visible: boolean) => void;
  setFreguesiasTileUrl: (url: string | null) => void;
  setFreguesiasSourceLayer: (layer: string | null) => void;
  setFreguesiasItemId: (id: string | null) => void;
  setFreguesiasOpacity: (opacity: number) => void;
  setConcelhosVisible: (visible: boolean) => void;
  setConcelhosTileUrl: (url: string | null) => void;
  setConcelhosSourceLayer: (layer: string | null) => void;
  setConcelhosItemId: (id: string | null) => void;
  setConcelhosOpacity: (opacity: number) => void;
  setRenVisible: (visible: boolean) => void;
  setRenTileUrl: (url: string | null) => void;
  setRenSourceLayer: (layer: string | null) => void;
  setRenItemId: (id: string | null) => void;
  setRenOpacity: (opacity: number) => void;
  setRenExclusoesVisible: (visible: boolean) => void;
  setRenExclusoesTileUrl: (url: string | null) => void;
  setRenExclusoesSourceLayer: (layer: string | null) => void;
  setRenExclusoesItemId: (id: string | null) => void;
  setRenExclusoesOpacity: (opacity: number) => void;
  setRanVisible: (visible: boolean) => void;
  setRanTileUrl: (url: string | null) => void;
  setRanSourceLayer: (layer: string | null) => void;
  setRanItemId: (id: string | null) => void;
  setRanOpacity: (opacity: number) => void;
  // OSM Context
  setRoadsVisible: (visible: boolean) => void;
  setRoadsOpacity: (opacity: number) => void;
  setBuildingsVisible: (visible: boolean) => void;
  setBuildingsOpacity: (opacity: number) => void;
  setLanduseVisible: (visible: boolean) => void;
  setLanduseOpacity: (opacity: number) => void;
  setWaterwaysVisible: (visible: boolean) => void;
  setWaterwaysOpacity: (opacity: number) => void;
  setAdminBoundsVisible: (visible: boolean) => void;
  setAdminBoundsOpacity: (opacity: number) => void;
}

export const useAtlasStore = create<AtlasState>((set) => ({
  viewMode: '2d',
  baseLayer: 'osm',
  orderPolygon: null,
  orderBounds: null,
  showPolygon: true,
  terrainExaggeration: 1.5,
  loading: false,
  error: null,
  rasterAvailable: {},
  layerLoading: {},
  loadLayer: null,
  mapCenter: [-8.0, 37.5],
  mapZoom: 7,

  createMode: false,
  drawing: false,
  draftPoints: [],
  draftGeometry: null,

  mdtVisible: false,
  mdtTileUrl: null,
  mdtDemTileUrl: null,
  mdtRescale: null,
  mdtZoomRange: null,
  mdtOpacity: 0.75,

  mdtLocalVisible: false,
  mdtLocalTileUrl: null,
  mdtLocalZoomRange: null,
  mdtLocalOpacity: 0.75,

  mdtDgtVisible: false,
  mdtDgtTileUrl: null,
  mdtDgtRescale: null,
  mdtDgtZoomRange: null,
  mdtDgtOpacity: 0.75,

  mdtDgtLocalVisible: false,
  mdtDgtLocalTileUrl: null,
  mdtDgtLocalZoomRange: null,
  mdtDgtLocalOpacity: 0.75,

  hillshadeVisible: false,
  hillshadeTileUrl: null,
  hillshadeZoomRange: null,
  hillshadeOpacity: 0.5,

  hillshadeLocalVisible: false,
  hillshadeLocalTileUrl: null,
  hillshadeLocalZoomRange: null,
  hillshadeLocalOpacity: 0.5,

  hillshadeDgtVisible: false,
  hillshadeDgtTileUrl: null,
  hillshadeDgtZoomRange: null,
  hillshadeDgtOpacity: 0.5,

  hillshadeDgtLocalVisible: false,
  hillshadeDgtLocalTileUrl: null,
  hillshadeDgtLocalZoomRange: null,
  hillshadeDgtLocalOpacity: 0.5,

  hillshadeHypsoVisible: false,
  hillshadeHypsoTileUrl: null,
  hillshadeHypsoZoomRange: null,
  hillshadeHypsoOpacity: 0.6,

  hillshadeHypsoLocalVisible: false,
  hillshadeHypsoLocalTileUrl: null,
  hillshadeHypsoLocalZoomRange: null,
  hillshadeHypsoLocalOpacity: 0.6,

  hillshadeHypsoDgtVisible: false,
  hillshadeHypsoDgtTileUrl: null,
  hillshadeHypsoDgtZoomRange: null,
  hillshadeHypsoDgtOpacity: 0.6,

  hillshadeHypsoDgtLocalVisible: false,
  hillshadeHypsoDgtLocalTileUrl: null,
  hillshadeHypsoDgtLocalZoomRange: null,
  hillshadeHypsoDgtLocalOpacity: 0.6,

  contoursVisible: false,
  contoursTileUrl: null,
  contoursSourceLayer: null,
  contoursItemId: null,
  contoursOpacity: 0.7,

  contoursDgtVisible: false,
  contoursDgtTileUrl: null,
  contoursDgtSourceLayer: null,
  contoursDgtItemId: null,
  contoursDgtOpacity: 0.7,

  slopeVisible: false,
  slopeTileUrl: null,
  slopeZoomRange: null,
  slopeOpacity: 0.5,

  aspectVisible: false,
  aspectTileUrl: null,
  aspectZoomRange: null,
  aspectOpacity: 0.35,

  geomorphonsVisible: false,
  geomorphonsTileUrl: null,
  geomorphonsZoomRange: null,
  geomorphonsOpacity: 0.65,

  erosionRiskVisible: false,
  erosionRiskTileUrl: null,
  erosionRiskZoomRange: null,
  erosionRiskOpacity: 0.5,

  windExpositionVisible: false,
  windExpositionTileUrl: null,
  windExpositionZoomRange: null,
  windExpositionOpacity: 0.6,
  windHillshadeTileUrl: null,
  windHillshadeZoomRange: null,
  windReliefTileUrl: null,
  windReliefZoomRange: null,

  streamsVisible: false,
  streamsTileUrl: null,
  streamsSourceLayer: null,
  streamsItemId: null,
  streamsOpacity: 0.78,

  flowAccVisible: false,
  flowAccTileUrl: null,
  flowAccZoomRange: null,
  flowAccOpacity: 0.99,

  basinsVisible: false,
  basinsTileUrl: null,
  basinsSourceLayer: null,
  basinsItemId: null,
  basinsOpacity: 0.5,

  keypointsVisible: false,
  keypointsTileUrl: null,
  keypointsSourceLayer: null,
  keypointsItemId: null,
  keypointsOpacity: 0.9,

  primaryValleysVisible: false,
  primaryValleysTileUrl: null,
  primaryValleysSourceLayer: null,
  primaryValleysItemId: null,
  primaryValleysOpacity: 0.5,

  primaryRidgesVisible: false,
  primaryRidgesTileUrl: null,
  primaryRidgesSourceLayer: null,
  primaryRidgesItemId: null,
  primaryRidgesOpacity: 0.5,

  floodZonesVisible: false,
  floodZonesTileUrl: null,
  floodZonesSourceLayer: null,
  floodZonesItemId: null,
  floodZonesOpacity: 0.55,

  ridgesVisible: false,
  ridgesTileUrl: null,
  ridgesSourceLayer: null,
  ridgesItemId: null,
  ridgesOpacity: 1.0,

  // Institutional. All layers start hidden (lazy-load UX, EB-56): the order
  // opens on a blank canvas with a "selecione uma camada" hint.
  cadastroDgtVisible: false,
  cadastroDgtTileUrl: null,
  cadastroDgtSourceLayer: 'cadastro_dgt',
  cadastroDgtItemId: null,
  cadastroDgtOpacity: 0.9,
  cadastroBounds: null,

  bupiRggVisible: false,
  bupiRggTileUrl: null,
  bupiRggSourceLayer: 'bupi_rgg',
  bupiRggItemId: null,
  bupiRggOpacity: 0.5,

  freguesiasVisible: false,
  freguesiasTileUrl: null,
  freguesiasSourceLayer: 'caop_admin',
  freguesiasItemId: null,
  freguesiasOpacity: 0.7,

  concelhosVisible: false,
  concelhosTileUrl: null,
  concelhosSourceLayer: 'caop_admin',
  concelhosItemId: null,
  concelhosOpacity: 0.8,

  renVisible: false,
  renTileUrl: null,
  renSourceLayer: 'ren',
  renItemId: null,
  renOpacity: 0.6,

  renExclusoesVisible: false,
  renExclusoesTileUrl: null,
  renExclusoesSourceLayer: 'ren',
  renExclusoesItemId: null,
  renExclusoesOpacity: 0.7,

  ranVisible: false,
  ranTileUrl: null,
  ranSourceLayer: 'ran',
  ranItemId: null,
  ranOpacity: 0.6,

  // OSM Context
  roadsVisible: false,
  roadsOpacity: 0.8,
  buildingsVisible: false,
  buildingsOpacity: 0.7,
  landuseVisible: false,
  landuseOpacity: 0.3,
  waterwaysVisible: false,
  waterwaysOpacity: 0.7,
  adminBoundsVisible: false,
  adminBoundsOpacity: 0.6,

  setViewMode: (mode) => set({ viewMode: mode }),
  setBaseLayer: (layer) => set({ baseLayer: layer }),
  setCreateMode: (b) => set(b ? { createMode: true } : { createMode: false, drawing: false, draftPoints: [], draftGeometry: null }),
  setDrawing: (b) => set(b ? { drawing: true, draftGeometry: null } : { drawing: false }),
  addDraftPoint: (pt) => set((s) => ({ draftPoints: [...s.draftPoints, pt] })),
  undoDraftPoint: () => set((s) => ({ draftPoints: s.draftPoints.slice(0, -1) })),
  clearDraft: () => set({ draftPoints: [], drawing: false, draftGeometry: null }),
  setDraftGeometry: (poly) => set({ draftGeometry: poly, draftPoints: [], drawing: false }),
  setOrderPolygon: (polygon) => set({ orderPolygon: polygon }),
  setOrderBounds: (bounds) => set({ orderBounds: bounds }),
  setShowPolygon: (show) => set({ showPolygon: show }),
  setRasterAvailable: (m) => set({ rasterAvailable: m }),
  setLayerLoading: (key, v) => set((s) => ({ layerLoading: { ...s.layerLoading, [key]: v } })),
  setLoadLayer: (fn) => set({ loadLayer: fn }),
  setTerrainExaggeration: (value) => set({ terrainExaggeration: value }),
  setLoading: (loading) => set({ loading: loading }),
  setError: (error) => set({ error: error }),
  setMapCenter: (center) => set({ mapCenter: center }),
  setMapZoom: (zoom) => set({ mapZoom: zoom }),
  setMdtVisible: (visible) => set({ mdtVisible: visible }),
  setMdtTileUrl: (url) => set({ mdtTileUrl: url }),
  setMdtDemTileUrl: (url) => set({ mdtDemTileUrl: url }),
  setMdtRescale: (rescale) => set({ mdtRescale: rescale }),
  setMdtZoomRange: (range) => set({ mdtZoomRange: range }),
  setMdtOpacity: (opacity) => set({ mdtOpacity: opacity }),
  setMdtLocalVisible: (visible) => set({ mdtLocalVisible: visible }),
  setMdtLocalTileUrl: (url) => set({ mdtLocalTileUrl: url }),
  setMdtLocalZoomRange: (range) => set({ mdtLocalZoomRange: range }),
  setMdtLocalOpacity: (opacity) => set({ mdtLocalOpacity: opacity }),
  setMdtDgtVisible: (visible) => set({ mdtDgtVisible: visible }),
  setMdtDgtTileUrl: (url) => set({ mdtDgtTileUrl: url }),
  setMdtDgtRescale: (rescale) => set({ mdtDgtRescale: rescale }),
  setMdtDgtZoomRange: (range) => set({ mdtDgtZoomRange: range }),
  setMdtDgtOpacity: (opacity) => set({ mdtDgtOpacity: opacity }),
  setMdtDgtLocalVisible: (visible) => set({ mdtDgtLocalVisible: visible }),
  setMdtDgtLocalTileUrl: (url) => set({ mdtDgtLocalTileUrl: url }),
  setMdtDgtLocalZoomRange: (range) => set({ mdtDgtLocalZoomRange: range }),
  setMdtDgtLocalOpacity: (opacity) => set({ mdtDgtLocalOpacity: opacity }),

  setHillshadeVisible: (visible) => set({ hillshadeVisible: visible }),
  setHillshadeTileUrl: (url) => set({ hillshadeTileUrl: url }),
  setHillshadeZoomRange: (range) => set({ hillshadeZoomRange: range }),
  setHillshadeOpacity: (opacity) => set({ hillshadeOpacity: opacity }),
  setHillshadeLocalVisible: (visible) => set({ hillshadeLocalVisible: visible }),
  setHillshadeLocalTileUrl: (url) => set({ hillshadeLocalTileUrl: url }),
  setHillshadeLocalZoomRange: (range) => set({ hillshadeLocalZoomRange: range }),
  setHillshadeLocalOpacity: (opacity) => set({ hillshadeLocalOpacity: opacity }),
  setHillshadeDgtVisible: (visible) => set({ hillshadeDgtVisible: visible }),
  setHillshadeDgtTileUrl: (url) => set({ hillshadeDgtTileUrl: url }),
  setHillshadeDgtZoomRange: (range) => set({ hillshadeDgtZoomRange: range }),
  setHillshadeDgtOpacity: (opacity) => set({ hillshadeDgtOpacity: opacity }),
  setHillshadeDgtLocalVisible: (visible) => set({ hillshadeDgtLocalVisible: visible }),
  setHillshadeDgtLocalTileUrl: (url) => set({ hillshadeDgtLocalTileUrl: url }),
  setHillshadeDgtLocalZoomRange: (range) => set({ hillshadeDgtLocalZoomRange: range }),
  setHillshadeDgtLocalOpacity: (opacity) => set({ hillshadeDgtLocalOpacity: opacity }),
  setHillshadeHypsoVisible: (visible) => set({ hillshadeHypsoVisible: visible }),
  setHillshadeHypsoTileUrl: (url) => set({ hillshadeHypsoTileUrl: url }),
  setHillshadeHypsoZoomRange: (range) => set({ hillshadeHypsoZoomRange: range }),
  setHillshadeHypsoOpacity: (opacity) => set({ hillshadeHypsoOpacity: opacity }),
  setHillshadeHypsoLocalVisible: (visible) => set({ hillshadeHypsoLocalVisible: visible }),
  setHillshadeHypsoLocalTileUrl: (url) => set({ hillshadeHypsoLocalTileUrl: url }),
  setHillshadeHypsoLocalZoomRange: (range) => set({ hillshadeHypsoLocalZoomRange: range }),
  setHillshadeHypsoLocalOpacity: (opacity) => set({ hillshadeHypsoLocalOpacity: opacity }),
  setHillshadeHypsoDgtVisible: (visible) => set({ hillshadeHypsoDgtVisible: visible }),
  setHillshadeHypsoDgtTileUrl: (url) => set({ hillshadeHypsoDgtTileUrl: url }),
  setHillshadeHypsoDgtZoomRange: (range) => set({ hillshadeHypsoDgtZoomRange: range }),
  setHillshadeHypsoDgtOpacity: (opacity) => set({ hillshadeHypsoDgtOpacity: opacity }),
  setHillshadeHypsoDgtLocalVisible: (visible) => set({ hillshadeHypsoDgtLocalVisible: visible }),
  setHillshadeHypsoDgtLocalTileUrl: (url) => set({ hillshadeHypsoDgtLocalTileUrl: url }),
  setHillshadeHypsoDgtLocalZoomRange: (range) => set({ hillshadeHypsoDgtLocalZoomRange: range }),
  setHillshadeHypsoDgtLocalOpacity: (opacity) => set({ hillshadeHypsoDgtLocalOpacity: opacity }),
  setContoursVisible: (visible) => set({ contoursVisible: visible }),
  setContoursTileUrl: (url) => set({ contoursTileUrl: url }),
  setContoursSourceLayer: (layer) => set({ contoursSourceLayer: layer }),
  setContoursItemId: (id) => set({ contoursItemId: id }),
  setContoursOpacity: (opacity) => set({ contoursOpacity: opacity }),
  setContoursDgtVisible: (visible) => set({ contoursDgtVisible: visible }),
  setContoursDgtTileUrl: (url) => set({ contoursDgtTileUrl: url }),
  setContoursDgtSourceLayer: (layer) => set({ contoursDgtSourceLayer: layer }),
  setContoursDgtItemId: (id) => set({ contoursDgtItemId: id }),
  setContoursDgtOpacity: (opacity) => set({ contoursDgtOpacity: opacity }),
  setSlopeVisible: (visible) => set({ slopeVisible: visible }),
  setSlopeTileUrl: (url) => set({ slopeTileUrl: url }),
  setSlopeZoomRange: (range) => set({ slopeZoomRange: range }),
  setSlopeOpacity: (opacity) => set({ slopeOpacity: opacity }),
  setAspectVisible: (visible) => set({ aspectVisible: visible }),
  setAspectTileUrl: (url) => set({ aspectTileUrl: url }),
  setAspectZoomRange: (range) => set({ aspectZoomRange: range }),
  setAspectOpacity: (opacity) => set({ aspectOpacity: opacity }),
  setGeomorphonsVisible: (visible) => set({ geomorphonsVisible: visible }),
  setGeomorphonsTileUrl: (url) => set({ geomorphonsTileUrl: url }),
  setGeomorphonsZoomRange: (range) => set({ geomorphonsZoomRange: range }),
  setGeomorphonsOpacity: (opacity) => set({ geomorphonsOpacity: opacity }),
  setErosionRiskVisible: (visible) => set({ erosionRiskVisible: visible }),
  setErosionRiskTileUrl: (url) => set({ erosionRiskTileUrl: url }),
  setErosionRiskZoomRange: (range) => set({ erosionRiskZoomRange: range }),
  setErosionRiskOpacity: (opacity) => set({ erosionRiskOpacity: opacity }),
  setWindExpositionVisible: (visible) => set({ windExpositionVisible: visible }),
  setWindExpositionTileUrl: (url) => set({ windExpositionTileUrl: url }),
  setWindExpositionZoomRange: (range) => set({ windExpositionZoomRange: range }),
  setWindExpositionOpacity: (opacity) => set({ windExpositionOpacity: opacity }),
  setWindHillshadeTileUrl: (url) => set({ windHillshadeTileUrl: url }),
  setWindReliefTileUrl: (url) => set({ windReliefTileUrl: url }),
  setWindReliefZoomRange: (range) => set({ windReliefZoomRange: range }),
  setWindHillshadeZoomRange: (range) => set({ windHillshadeZoomRange: range }),
  setStreamsVisible: (visible) => set({ streamsVisible: visible }),
  setStreamsTileUrl: (url) => set({ streamsTileUrl: url }),
  setStreamsSourceLayer: (layer) => set({ streamsSourceLayer: layer }),
  setStreamsItemId: (id) => set({ streamsItemId: id }),
  setStreamsOpacity: (opacity) => set({ streamsOpacity: opacity }),
  setFlowAccVisible: (visible) => set({ flowAccVisible: visible }),
  setFlowAccTileUrl: (url) => set({ flowAccTileUrl: url }),
  setFlowAccZoomRange: (range) => set({ flowAccZoomRange: range }),
  setFlowAccOpacity: (opacity) => set({ flowAccOpacity: opacity }),
  setBasinsVisible: (visible) => set({ basinsVisible: visible }),
  setBasinsTileUrl: (url) => set({ basinsTileUrl: url }),
  setBasinsSourceLayer: (layer) => set({ basinsSourceLayer: layer }),
  setBasinsItemId: (id) => set({ basinsItemId: id }),
  setBasinsOpacity: (opacity) => set({ basinsOpacity: opacity }),
  setKeypointsVisible: (visible) => set({ keypointsVisible: visible }),
  setKeypointsTileUrl: (url) => set({ keypointsTileUrl: url }),
  setKeypointsSourceLayer: (layer) => set({ keypointsSourceLayer: layer }),
  setKeypointsItemId: (id) => set({ keypointsItemId: id }),
  setKeypointsOpacity: (opacity) => set({ keypointsOpacity: opacity }),
  setPrimaryValleysVisible: (visible) => set({ primaryValleysVisible: visible }),
  setPrimaryValleysTileUrl: (url) => set({ primaryValleysTileUrl: url }),
  setPrimaryValleysSourceLayer: (layer) => set({ primaryValleysSourceLayer: layer }),
  setPrimaryValleysItemId: (id) => set({ primaryValleysItemId: id }),
  setPrimaryValleysOpacity: (opacity) => set({ primaryValleysOpacity: opacity }),
  setPrimaryRidgesVisible: (visible) => set({ primaryRidgesVisible: visible }),
  setPrimaryRidgesTileUrl: (url) => set({ primaryRidgesTileUrl: url }),
  setPrimaryRidgesSourceLayer: (layer) => set({ primaryRidgesSourceLayer: layer }),
  setPrimaryRidgesItemId: (id) => set({ primaryRidgesItemId: id }),
  setPrimaryRidgesOpacity: (opacity) => set({ primaryRidgesOpacity: opacity }),
  setFloodZonesVisible: (visible) => set({ floodZonesVisible: visible }),
  setFloodZonesTileUrl: (url) => set({ floodZonesTileUrl: url }),
  setFloodZonesSourceLayer: (layer) => set({ floodZonesSourceLayer: layer }),
  setFloodZonesItemId: (id) => set({ floodZonesItemId: id }),
  setFloodZonesOpacity: (opacity) => set({ floodZonesOpacity: opacity }),
  setRidgesVisible: (visible) => set({ ridgesVisible: visible }),
  setRidgesTileUrl: (url) => set({ ridgesTileUrl: url }),
  setRidgesSourceLayer: (layer) => set({ ridgesSourceLayer: layer }),
  setRidgesItemId: (id) => set({ ridgesItemId: id }),
  setRidgesOpacity: (opacity) => set({ ridgesOpacity: opacity }),
  // Institutional
  setCadastroDgtVisible: (visible) => set({ cadastroDgtVisible: visible }),
  setCadastroDgtTileUrl: (url) => set({ cadastroDgtTileUrl: url }),
  setCadastroDgtSourceLayer: (layer) => set({ cadastroDgtSourceLayer: layer }),
  setCadastroDgtItemId: (id) => set({ cadastroDgtItemId: id }),
  setCadastroDgtOpacity: (opacity) => set({ cadastroDgtOpacity: opacity }),
  setCadastroBounds: (bounds) => set({ cadastroBounds: bounds }),
  setBupiRggVisible: (visible) => set({ bupiRggVisible: visible }),
  setBupiRggTileUrl: (url) => set({ bupiRggTileUrl: url }),
  setBupiRggSourceLayer: (layer) => set({ bupiRggSourceLayer: layer }),
  setBupiRggItemId: (id) => set({ bupiRggItemId: id }),
  setBupiRggOpacity: (opacity) => set({ bupiRggOpacity: opacity }),
  setFreguesiasVisible: (visible) => set({ freguesiasVisible: visible }),
  setFreguesiasTileUrl: (url) => set({ freguesiasTileUrl: url }),
  setFreguesiasSourceLayer: (layer) => set({ freguesiasSourceLayer: layer }),
  setFreguesiasItemId: (id) => set({ freguesiasItemId: id }),
  setFreguesiasOpacity: (opacity) => set({ freguesiasOpacity: opacity }),
  setConcelhosVisible: (visible) => set({ concelhosVisible: visible }),
  setConcelhosTileUrl: (url) => set({ concelhosTileUrl: url }),
  setConcelhosSourceLayer: (layer) => set({ concelhosSourceLayer: layer }),
  setConcelhosItemId: (id) => set({ concelhosItemId: id }),
  setConcelhosOpacity: (opacity) => set({ concelhosOpacity: opacity }),
  setRenVisible: (visible) => set({ renVisible: visible }),
  setRenTileUrl: (url) => set({ renTileUrl: url }),
  setRenSourceLayer: (layer) => set({ renSourceLayer: layer }),
  setRenItemId: (id) => set({ renItemId: id }),
  setRenOpacity: (opacity) => set({ renOpacity: opacity }),
  setRenExclusoesVisible: (visible) => set({ renExclusoesVisible: visible }),
  setRenExclusoesTileUrl: (url) => set({ renExclusoesTileUrl: url }),
  setRenExclusoesSourceLayer: (layer) => set({ renExclusoesSourceLayer: layer }),
  setRenExclusoesItemId: (id) => set({ renExclusoesItemId: id }),
  setRenExclusoesOpacity: (opacity) => set({ renExclusoesOpacity: opacity }),
  setRanVisible: (visible) => set({ ranVisible: visible }),
  setRanTileUrl: (url) => set({ ranTileUrl: url }),
  setRanSourceLayer: (layer) => set({ ranSourceLayer: layer }),
  setRanItemId: (id) => set({ ranItemId: id }),
  setRanOpacity: (opacity) => set({ ranOpacity: opacity }),
  // OSM Context
  setRoadsVisible: (visible) => set({ roadsVisible: visible }),
  setRoadsOpacity: (opacity) => set({ roadsOpacity: opacity }),
  setBuildingsVisible: (visible) => set({ buildingsVisible: visible }),
  setBuildingsOpacity: (opacity) => set({ buildingsOpacity: opacity }),
  setLanduseVisible: (visible) => set({ landuseVisible: visible }),
  setLanduseOpacity: (opacity) => set({ landuseOpacity: opacity }),
  setWaterwaysVisible: (visible) => set({ waterwaysVisible: visible }),
  setWaterwaysOpacity: (opacity) => set({ waterwaysOpacity: opacity }),
  setAdminBoundsVisible: (visible) => set({ adminBoundsVisible: visible }),
  setAdminBoundsOpacity: (opacity) => set({ adminBoundsOpacity: opacity }),
}));

// Expose store on window for debugging and imperative access
(window as any).__atlasStore = useAtlasStore;
